<!--
  Labelled grouping container — the `sbi.ui.section(title, body, opts?)`
  primitive. Renders `<section>` with a heading, optional small caption,
  and an optional long-form description paragraph rendered inline as
  part of the design (used to be a tooltip on a ⓘ icon — moved into
  the visible layout so context isn't hidden behind hover).

  Header order: title → caption (small sub-heading) → description
  (deck-style paragraph). Each is opt-in; only the title is required.

  Heading level is fixed `<h3>` regardless of nesting depth (Q10
  decision). Nested sections will produce a flat outline — accepted
  accessibility trade-off; documented in the plan. Revisit if SR-user
  feedback surfaces.

  Embedder theming:
    - data-part="section" / "section-title" / "section-caption" /
      "section-description" / "section-body" — shadow-DOM-safe
      targeting hooks.
    - --lucent-ui-section-title-fg       — title colour (default: `--card-ink`)
    - --lucent-ui-section-caption-fg     — caption colour (default: `--card-muted`)
    - --lucent-ui-section-description-fg — description colour (default: `--card-ink`)
    - --lucent-ui-section-gap            — vertical gap between header and body
    - --lucent-section-bg                — section surface fill (default: transparent)
    - --lucent-section-border            — section border shorthand (default: none)
    - --lucent-section-radius            — section corner radius (default: 0)
    - --lucent-section-shadow            — section box-shadow (default: none)
    - --lucent-section-pad               — section padding shorthand (default: 0)
    - --lucent-section-header-rule       — header bottom border shorthand (default: none)
    - --lucent-section-header-pad-bottom — space between rule and body (default: 0)
    - --lucent-section-title-align       — title text-align (default: left)
    - --lucent-section-description-accent — accent rule on the description
        block (default: 2px solid var(--primary, currentColor)).
-->

<script setup lang="ts">
import type { ChangeValue } from "../sbi/protocol";
import type { TreeNode } from "../sbi/validate-render-op";
import InsightNode from "./InsightNode.vue";
import LucentIcon from "./LucentIcon.vue";

defineProps<{
  title: string;
  caption?: string;
  description?: string;
  icon?: string;
  iconPosition?: "leading" | "trailing";
  children: ReadonlyArray<TreeNode>;
  loading?: boolean;
  formats?: Record<string, string>;
}>();

defineEmits<{
  (e: "click-target", id: string, datum?: Record<string, unknown>, rowIndex?: number): void;
  (e: "change-target", id: string, value: ChangeValue): void;
  /** Forwarded from a descendant modal drilldown's X / Esc / backdrop. */
  (e: "chrome-click-target", id: string): void;
  /** Forwarded from descendant accordions so EmbedHost can surface the DOM event. */
  (e: "accordion-toggle", summary: string, open: boolean): void;
  /** Forwarded from descendant callouts so EmbedHost can surface the DOM event. */
  (e: "callout-rendered", kind: string, title?: string): void;
}>();
</script>

<template>
  <section class="section" data-part="section">
    <header class="section-header">
      <h3 class="section-title" data-part="section-title">
        <LucentIcon
          v-if="icon && (iconPosition ?? 'leading') === 'leading'"
          :name="icon"
          :size="18"
          class="section-title-icon section-title-icon--leading"
        />
        <span>{{ title }}</span>
        <LucentIcon
          v-if="icon && iconPosition === 'trailing'"
          :name="icon"
          :size="18"
          class="section-title-icon section-title-icon--trailing"
        />
      </h3>
      <p v-if="caption" class="section-caption" data-part="section-caption">
        {{ caption }}
      </p>
      <p
        v-if="description"
        class="section-description"
        data-part="section-description"
      >
        {{ description }}
      </p>
    </header>
    <div class="section-body" data-part="section-body">
      <InsightNode
        v-for="(child, i) in children"
        :key="i"
        :node="child"
        :loading="loading"
        :formats="formats"
        @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
        @change-target="(id, value) => $emit('change-target', id, value)"
        @chrome-click-target="(id) => $emit('chrome-click-target', id)"
        @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
        @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
      />
    </div>
  </section>
</template>

<style scoped>
.section {
  display: flex;
  flex-direction: column;
  gap: var(--lucent-ui-section-gap, var(--space-3));
  margin: var(--space-4) 0;
  min-width: 0;
  /* Section-as-card surface. Defaults are flat (transparent fill,
     no border, no shadow, no padding) so existing themes are
     unaffected; opt in by setting any of these. */
  background: var(--lucent-section-bg, transparent);
  border: var(--lucent-section-border, none);
  border-radius: var(--lucent-section-radius, 0);
  box-shadow: var(--lucent-section-shadow, none);
  padding: var(--lucent-section-pad, 0);
}
.section-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  /* Optional hairline under the title block — e.g. Stripe / Slack
     "section divider" treatment. Default `none` so themes that don't
     opt in render unchanged. */
  border-bottom: var(--lucent-section-header-rule, none);
  padding-bottom: var(--lucent-section-header-pad-bottom, 0);
}
.section-title {
  /* Themer-facing: --lucent-font-section-title /
     --lucent-tracking-section-title / --lucent-case-section-title /
     --lucent-section-title-align. */
  font: var(
    --lucent-font-section-title,
    600 var(--_lucent-section-title-size, 16px)/1.25 system-ui, sans-serif
  );
  letter-spacing: var(--lucent-tracking-section-title, -0.015em);
  text-transform: var(--lucent-case-section-title, none);
  text-align: var(--lucent-section-title-align, left);
  margin: 0;
  color: var(--lucent-ui-section-title-fg, var(--card-ink));
  /* Block-level flex (not inline-flex) so a long title wraps inside
     the section instead of overflowing it, and so the align token
     positions the title — `text-align` alone can't move a flex item.
     `left` / `center` / `right` are valid for both properties. */
  display: flex;
  justify-content: var(--lucent-section-title-align, left);
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.section-title > span {
  min-width: 0;
  overflow-wrap: anywhere;
  hyphens: auto;
}
.section-title-icon {
  flex: 0 0 auto;
  align-self: center;
  color: currentColor;
  opacity: 0.75;
}
.section-caption {
  overflow-wrap: anywhere;
  font-size: 13px;
  margin: 0;
  color: var(--lucent-ui-section-caption-fg, var(--card-muted));
  line-height: 1.4;
}
/* Long-form description — visible deck paragraph below the caption.
   Slightly larger and less muted than the caption so the two read as
   a hierarchy ("short label → expanded context") rather than a pair
   of equal-weight subtitles. A leading mint accent rule echoes the
   Lucent brand cue from the dashboard hero band; embedders override
   via `--lucent-section-description-accent`. */
.section-description {
  overflow-wrap: anywhere;
  margin: 6px 0 0;
  padding-left: 12px;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--lucent-ui-section-description-fg, var(--card-ink));
  border-left: var(--lucent-section-description-accent, 2px solid var(--primary, currentColor));
  max-width: 70ch;
}
.section-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  /* Container slot: nested components' @container queries measure
     this cell, not the page. Safe because the slot is stretched by
     its parent, never sized to its content. */
  container-type: inline-size;
}
</style>
